import React, { useContext, useEffect, useReducer, useRef, useCallback } from 'react';
import {
	VideoCategoriesContext,
	videoCategoriesInitialState,
	IFilterValues,
	TVideoCategoriesCountOfItems,
	TVideoCategoriesSortBy,
	TTypeOfModalWindow,
} from './videoCategoriesContext';
import { AppContext, IAppContext } from 'appState/appContext';
import { videoCategoriesReducer } from './videoCategoriesReducer';
import { VideoCategoriesActionTypes } from './videoCategoriesActions';
import { useFetchData } from 'hooks/useFetchData';
import { getPaginatedVideoCategories, deleteVideoCategory } from 'backend/videos';
import { AxiosResponse } from 'axios';
import { IVideoCategories, IGetPaginatedVideoCategories } from 'types';

const VideoCategoriesState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(videoCategoriesReducer, videoCategoriesInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<AxiosResponse<IVideoCategories>, IGetPaginatedVideoCategories>(
		isMounted.current ? getPaginatedVideoCategories : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchByName: state.searchString ? state.searchString : null,
			sortByName: state.sortBy === 'NameA-Z' || state.sortBy === 'NameZ-A' ? state.sortBy : null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: VideoCategoriesActionTypes.SET_VIDEO_CATEGORIES,
				payload: {
					videoCategories: response.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
		}
	}, [response]);

	const deleteSelectedItem = useCallback(async () => {
		try {
			showLoader();
			await deleteVideoCategory(state.IDForDeletion);
			const filteredVideoCategories = state.videoCategories.videoCategories.filter(
				(el) => el.id !== state.IDForDeletion,
			);
			dispatch({
				type: VideoCategoriesActionTypes.SET_VIDEO_CATEGORIES,
				payload: {
					videoCategories: {
						videoCategoriesCount: state.videoCategories.videoCategoriesCount - 1,
						videoCategories: filteredVideoCategories,
					},
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			dispatch({
				type: VideoCategoriesActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	}, [showLoader, hideLoader, state.IDForDeletion, state.videoCategories]);

	const changeSearchString = (value: string) => {
		dispatch({
			type: VideoCategoriesActionTypes.CHANGE_SEARCH_STRING,
			payload: {
				searchString: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: VideoCategoriesActionTypes.RESET_SELECTED_CONTROLS });

	const setSortBy = (sortBy: TVideoCategoriesSortBy) => {
		dispatch({
			type: VideoCategoriesActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setCountOfItems = (count: TVideoCategoriesCountOfItems) => {
		dispatch({
			type: VideoCategoriesActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: VideoCategoriesActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const setIDForDeletion = (value: string) => {
		dispatch({
			type: VideoCategoriesActionTypes.SET_ID_FOR_DELETION,
			payload: {
				IDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: VideoCategoriesActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setFiltersFromQueryString = (value: IFilterValues) => {
		dispatch({
			type: VideoCategoriesActionTypes.SET_FILTERS_FROM_QUERY_STRING,
			payload: {
				filters: value,
			},
		});
	};

	useEffect(() => {
		isMounted.current = true;
		refetch();
	}, [state.currentPage, state.countOfItems, state.searchString, state.sortBy]);

	return (
		<VideoCategoriesContext.Provider
			value={{
				deleteSelectedItem,
				changeSearchString,
				resetSelectedControls,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setIDForDeletion,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				videoCategories: state.videoCategories,
				searchString: state.searchString,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				IDForDeletion: state.IDForDeletion,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</VideoCategoriesContext.Provider>
	);
};

export default VideoCategoriesState;
